// Hook personalizado para manejo optimizado de renovación de tokens
// Previene llamadas múltiples y concurrentes al endpoint de refresh

import { useState, useCallback } from 'react';
import { supabase } from '../config/supabase';

// Constante para controlar logs de desarrollo
const DEBUG = process.env.NODE_ENV === 'development';

// Variables globales para controlar concurrencia a nivel de aplicación
let isRefreshingGlobal = false;
let globalRefreshPromise = null;
let lastGlobalRefresh = 0;

/**
 * Hook para manejar renovación de tokens con control de concurrencia mejorado
 * Simplificado para reducir complejidad y mejorar mantenibilidad
 */
export const useTokenRefresh = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Función para renovar token con control de concurrencia optimizado
   */
  const refreshToken = useCallback(async () => {
    // Previene múltiples llamadas concurrentes
    if (isRefreshingGlobal && globalRefreshPromise) {
      if (DEBUG) console.log('Refresh ya en progreso, retornando promise existente');
      return globalRefreshPromise;
    }

    // Previene llamadas muy frecuentes (mínimo 60 segundos)
    const now = Date.now();
    if (now - lastGlobalRefresh < 60000) {
      if (DEBUG) console.log('Refresh muy reciente, omitiendo llamada');
      return null;
    }

    // Establecer controles
    isRefreshingGlobal = true;
    setIsRefreshing(true);
    lastGlobalRefresh = now;

    globalRefreshPromise = (async () => {
      try {
        if (DEBUG) console.log('Iniciando refresh de token...');
        const { data, error } = await supabase.auth.refreshSession();
        
        if (error) {
          if (DEBUG) console.error('Error al renovar token:', error);
          throw error;
        }

        if (DEBUG) console.log('Token renovado exitosamente');
        return data;
      } catch (error) {
        if (DEBUG) console.error('Falló la renovación del token:', error);
        throw error;
      } finally {
        // Limpiar controles
        isRefreshingGlobal = false;
        setIsRefreshing(false);
        globalRefreshPromise = null;
      }
    })();

    return globalRefreshPromise;
  }, []);

  /**
   * Verifica si el token necesita renovación
   */
  const shouldRefreshToken = useCallback((session) => {
    if (!session?.expires_at) return false;
    
    const expiresAt = new Date(session.expires_at * 1000);
    const now = new Date();
    const timeUntilExpiry = expiresAt.getTime() - now.getTime();
    
    // Renovar si faltan menos de 30 minutos
    return timeUntilExpiry < 30 * 60 * 1000;
  }, []);

  /**
   * Función principal que verifica y renueva si es necesario
   */
  const ensureValidToken = useCallback(async (session) => {
    if (!session) return null;
    
    if (shouldRefreshToken(session)) {
      try {
        if (DEBUG) console.log('Token próximo a expirar, iniciando renovación...');
        const refreshedSession = await refreshToken();
        
        if (refreshedSession?.session && refreshedSession.session !== session) {
          if (DEBUG) console.log('Token renovado exitosamente');
          return refreshedSession.session;
        }
        
        return session;
      } catch (error) {
        if (DEBUG) console.error('No se pudo renovar el token:', error);
        return session;
      }
    }
    
    return session;
  }, [shouldRefreshToken, refreshToken]);

  return {
    refreshToken,
    isRefreshing: isRefreshing || isRefreshingGlobal,
    shouldRefreshToken,
    ensureValidToken,
  };
};
